import {
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import * as Yup from 'yup';

import CustomsItem from './customsItem';
import { useShipping } from './shippingProvider';

const ContentTypeMaps = Object.freeze({
  merchandise: { label: 'Merchandise', value: 'merchandise' },
  gift: { label: 'Gift', value: 'gift' },
  sample: { label: 'Sample', value: 'sample' },
  documents: { label: 'Documents', value: 'documents' },
});

export const DefaultCustomsItem = Object.freeze({
  hs_tariff_number: '3926.90',
  description: 'PLASTICS AND ARTICLES THEREOF---OTHER',
  value: null,
  quantity: null,
  weight: null,
  origin_country: 'US',
  currency: 'USD',
});

const ValidationSchema = Yup.object({
  contents_type: Yup.string().required('Content type is required'),
  contents_explanation: Yup.string().when('contents_type', {
    is: 'other',
    then: Yup.string().required('Explanation of content is required when content type is "other"'),
  }),
  restriction_type: Yup.string().required('Restriction type is required'),
  customs_certify: Yup.boolean().oneOf([true], 'You must certify the information provided is accurate'),
  customs_signer: Yup.string().required('Signature is required'),
  non_delivery_option: Yup.string().required('Non-delivery action is required'),
  eel_pfc: Yup.string().required('EEL or PFC number is required').test(
    'is-valid-aes-or-noeei',
    'Must be "NOEEI 30.37(a)", "NOEEI 30.36", or a valid AES ITN like "AES X20240502123456"',
    (value) => {
      if (!value) return false;
      const noeeiPattern = /^NOEEI 30\.(37\(a\)|36)$/;
      const aesPattern = /^AES X\d{14}$/;
      return noeeiPattern.test(value) || aesPattern.test(value);
    },
  ),
  customsItems: Yup.array().of(
    Yup.object().shape({
      hs_tariff_number: Yup.string().required('HS Tariff Number is required'),
      description: Yup.string().required('Description of item is required'),
      value: Yup.number().required('Value is required').min(0.01, 'Total value must be greater than 0'),
      quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be greater than 0'),
      weight: Yup.number().required('Weight is required').min(0.01, 'Weight must be greater than 0'),
      origin_country: Yup.string().required('Origin country is required'),
      currency: Yup.string().required('Currency is required'),
    }),
  ).min(1, 'At least one customs item is required'),
});

export default function CustomsForm({ onNext, onPrev, disableItemEdit = false }) {
  const {
    isLoading,
    customsInfo: defaultInfo,
    setCustomsInfo,
    customsItems: defaultItems,
    setCustomsItems,
    selectedShipFor,
    toAddress,
  } = useShipping();

  const handleBack = useCallback(() => {
    setCustomsInfo(null);
    setCustomsItems(null);
    onPrev();
  }, [onPrev, setCustomsInfo, setCustomsItems]);

  const isShippingSample = selectedShipFor?.value === 'Sample part';
  const isCanada = toAddress?.country === 'CA';

  const getDefaultEEL = useCallback(() => {
    if (isShippingSample) return 'NOEEI 30.37(a)';
    if (isCanada === 'CA') return 'NOEEI 30.36';
    return '';
  }, [isShippingSample, isCanada]);

  return (
    <Formik
      initialValues={{
        // Customs Info Form: https://docs.easypost.com/guides/customs-guide#step-2-create-a-customs-info-form
        ...(defaultInfo || {
          contents_type: isShippingSample ? 'sample' : 'merchandise',
          contents_explanation: 'Custom molded articles of plastic', // required if contents_type is 'other'
          restriction_type: 'none',
          customs_certify: false,
          customs_signer: '',
          non_delivery_option: 'return',
          eel_pfc: getDefaultEEL(), // required, get from Fedex or https://ace.cbp.gov/
        }),

        // CustomsItems: https://docs.easypost.com/guides/customs-guide#create-customsitems
        customsItems: defaultItems || [DefaultCustomsItem],
      }}
      validationSchema={ValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        const castedValues = ValidationSchema.cast(values);
        const {
          customsItems, ...customsInfo
        } = castedValues;

        // set data to shipping provider
        setCustomsInfo(customsInfo);
        setCustomsItems(customsItems);
        setSubmitting(false);

        // go next step
        onNext();
      }}
    >
      {({
        values,
        errors,
        // isValid,
        isSubmitting,
        setFieldValue,
        handleSubmit,
      }) => (
        <SpaceBetween size="m">
          <Container header={<Header variant="h3">Customs Information</Header>}>
            <ColumnLayout columns={2}>
              <FormField label="Content Type" errorText={errors.contents_type}>
                <Select
                  disabled
                  placeholder="Choose package contents"
                  selectedOption={ContentTypeMaps[values.contents_type]}
                  onChange={({ detail }) => setFieldValue('contents_type', detail.selectedOption.value)}
                  options={Object.values(ContentTypeMaps)}
                />
              </FormField>
              <FormField label="Explanation of Content" errorText={errors.contents_explanation}>
                <Input
                  value={values.contents_explanation}
                  onChange={({ detail }) => { setFieldValue('contents_explanation', detail.value); }}
                />
              </FormField>
              <FormField
                label="EEL or PFC Number"
                errorText={errors.eel_pfc}
                constraintText={(
                  <div>
                    {isShippingSample && (
                      <div>
                        Note: the value of this shipment
                        {' '}
                        <b>MUST</b>
                        {' '}
                        be less than $2500. This should always be the case for sample parts.
                      </div>
                    )}
                    {isCanada && (
                      <div>NO EEI 30.36 is used for most Canadian shipments.</div>
                    )}
                    {!isShippingSample && !isCanada && (
                      <div>
                        Ask someone with access to Parallel&apos;s FedEx Export AgentFile account
                        to generate EEI information.
                      </div>
                    )}
                  </div>
                )}
              >
                <Input
                  value={values.eel_pfc}
                  onChange={({ detail }) => { setFieldValue('eel_pfc', detail.value); }}
                />
              </FormField>
            </ColumnLayout>
          </Container>

          <Container header={<Header variant="h3">Customs Items</Header>}>
            <SpaceBetween direction="vertical" size="s">
              {values.customsItems.map((item, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <CustomsItem key={idx} itemIdx={idx} disableItemEdit={disableItemEdit} />
              ))}

              {!disableItemEdit && (
                <Button
                  formAction="none"
                  onClick={() => setFieldValue('customsItems', [...values.customsItems, DefaultCustomsItem])}
                >
                  Add new customs item
                </Button>
              )}
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h3">Signature</Header>}>
            <ColumnLayout columns={1}>
              <FormField errorText={errors.customs_certify}>
                <Checkbox
                  onChange={({ detail }) => setFieldValue('customs_certify', detail.checked)}
                  checked={values.customs_certify}
                >
                  I certify that the information provided is accurate
                </Checkbox>
              </FormField>
              <FormField label="Signature" errorText={errors.customs_signer}>
                <Input
                  value={values.customs_signer}
                  placeholder="Type your full name"
                  onChange={({ detail }) => { setFieldValue('customs_signer', detail.value); }}
                />
              </FormField>
            </ColumnLayout>
          </Container>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <Button onClick={handleBack}>Back</Button>
            <Button variant="primary" loading={isLoading || isSubmitting} onClick={handleSubmit}>Next</Button>
          </div>
        </SpaceBetween>
      )}
    </Formik>
  );
}

CustomsForm.propTypes = {
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  disableItemEdit: PropTypes.bool,
};
